import { useUserLoggedIn } from '~/modules/user/hook/use-user';
import { useRef } from 'react';
import { useImpression, useTracker } from '@archipro-website/tracker';
import { useSetLoginModal } from '~/modules-rocco/user/hook/use-open-login-modal';
import { useHeroMarket } from '~/ui-rocco/pages/home-page/sections/hero-markets';
import productPhoto from '~/ui-rocco/pages/home-page/images/feature-strip/photo-products.jpg';
import professionalPhoto from '~/ui-rocco/pages/home-page/images/feature-strip/photo-professionals.jpg';
import projectPhoto from '~/ui-rocco/pages/home-page/images/feature-strip/photo-projects.jpg';

/**
 * What an account unlocks, as three tiles: a product, a professional and a
 * project — the site's three libraries, in the order the nav lists them.
 */
const LIBRARY_TILES = [
    { label: 'Products', src: productPhoto },
    { label: 'Professionals', src: professionalPhoto },
    { label: 'Projects', src: projectPhoto },
] as const;

/**
 * Interspersed sign-in prompt for the directory browse pages. Shown to
 * logged-out users only; renders nothing once signed in. Sign in / Sign up
 * open the login/sign-up modal in place (the form carries both flows), so the
 * user never leaves the grid they were browsing.
 *
 * Rendered as an editorial image banner — the market hero photo under the
 * detail-closing gradient treatment (HeroSection variant="detail") — so the
 * injection reads as a designed break in the grid rather than a bare bordered
 * box. The photo/gradient carry their own contrast, so the block is
 * theme-independent (no light/dark variants).
 */
export const DirectoryPromoBanner = () => {
    const { isLoggedIn } = useUserLoggedIn();
    const { setLoginModal } = useSetLoginModal();
    const { image, mobileImage } = useHeroMarket();
    const tracker = useTracker();
    const bannerRef = useRef<HTMLDivElement>(null);

    // A placement with no seen-count is a click total with no denominator, so
    // a banner shown on every page outscores one shown once purely by being
    // shown more. Hooks stay above the logged-in return to keep their order.
    useImpression({
        ref: bannerRef,
        disable: isLoggedIn,
        onImpression: async (url: URL) => {
            await tracker.log('apConnectBannerView', {
                url,
                data: {
                    Source: 'directoryPromoBanner',
                    ExtraData: JSON.stringify({
                        surface: 'directoryPromoBanner',
                    }),
                },
            });
        },
    });

    if (isLoggedIn) {
        return null;
    }

    const openLoginModal = () => {
        void tracker.log('apConnectButtonClick', {
            url: new URL(window.location.href),
            data: {
                Source: 'directoryPromoBanner',
                ExtraData: JSON.stringify({ surface: 'directoryPromoBanner' }),
            },
        });
        setLoginModal({ authSource: 'directoryPromoBanner', isOpen: true });
    };

    return (
        <div ref={bannerRef} className="relative overflow-hidden rounded-tile">
            <picture>
                <source media="(min-width: 768px)" srcSet={image} />
                <img
                    src={mobileImage}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    className="absolute inset-0 size-full object-cover"
                />
            </picture>
            {/* Bottom gradient for legibility — same recipe as the homepage /
                detail-closing hero. */}
            <div
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 h-3/4 bg-linear-to-t from-[#131313] from-[6.207%] to-transparent to-[70.169%] opacity-[0.89]"
            />
            {/* The three libraries, ranged right and centred on the band.
                Desktop only: on a phone the copy takes the whole card. */}
            <ul className="absolute inset-y-0 right-10 m-0 hidden list-none items-center gap-[1rem] p-0 md:flex">
                {LIBRARY_TILES.map((tile) => (
                    <li
                        key={tile.label}
                        className="flex w-[12.5rem] flex-col gap-[0.625rem]"
                    >
                        <div className="aspect-[3/4] w-full overflow-hidden rounded-[1rem] bg-white/10 shadow-[0_1.5rem_3rem_rgba(0,0,0,0.35)]">
                            <img
                                src={tile.src}
                                alt=""
                                aria-hidden="true"
                                loading="lazy"
                                className="size-full object-cover"
                            />
                        </div>
                        <p className="m-0 font-sans text-[0.8125rem] leading-none font-medium tracking-[0.02em] text-[#e5e5e5]">
                            {tile.label}
                        </p>
                    </li>
                ))}
            </ul>
            <div className="relative flex min-h-[18rem] flex-col justify-end gap-5 p-6 md:min-h-[26rem] md:p-10 md:pr-[44rem]">
                <div className="flex flex-col gap-2">
                    <h3 className="m-0 text-[1.75rem] leading-[1.15] font-normal tracking-[-0.02rem] text-[#e5e5e5] md:text-[2.5rem]">
                        Unlock all of ArchiPro
                    </h3>
                    <p className="m-0 max-w-[36rem] text-sm text-[#fafafa]/90 md:text-base">
                        Create a free account to save products and projects to
                        your boards, download spec sheets and files, and message
                        the professionals behind them. All in the same library
                        architects and interior designers work from.
                    </p>
                </div>
                <div className="flex items-center gap-4">
                    <button
                        type="button"
                        onClick={openLoginModal}
                        className="flex items-center rounded-[var(--radius-btn-m)] bg-[#f2f2f2] px-7 py-2.5 text-sm font-medium text-[#323232] transition-colors hover:bg-offwhite max-md:min-h-11"
                    >
                        Sign in
                    </button>
                    <button
                        type="button"
                        onClick={openLoginModal}
                        className="flex items-center text-sm font-medium text-offwhite underline max-md:min-h-11 max-md:px-2"
                    >
                        Sign up
                    </button>
                </div>
            </div>
        </div>
    );
};
